import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {
  Dialog,
  DialogContent,
  Heading,
  LoadingOverlay,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useTaopix} from '../../Context/TaopixContext';
import DeviceUploader from './DeviceUploader';
import Logo from '../../AppHeader/Logo';
import useSettings from '../../../Hooks/useSettings';

const UploadFromDevice = () => {
  // loading the settings
  const settings = useSettings();

  // pull in the app status
  const app = useTaopix();

  // get the i18n instance
  const {t} = useTranslation(['uploadexternalassets']);

  // set the title of the page based on the provided title in the settings
  if (settings.state.project.name) {
    document.title = t('str_TitleExternalUploadPictureUploader');
  }

  const [expired, setExpired] = useState(false);

  // use a ref to hold our timeout
  const intervalRef = useRef<NodeJS.Timeout>();

  const windowClassNames = classNames(
    'absolute',
    'top-0',
    'left-0',
    'w-full',
    'h-full',
    'grid',
    'grid-rows-[auto_1fr]',
    'p-lg',
    'justify-items-center',
    'box-border',
    'text-center'
  );

  const contentPanelClasses = classNames(
    'flex',
    'flex-col',
    'space-y-md',
    'self-center',
    'items-center',
    'p-lg',
    'min-h-[200rem]',
    'w-[min(300rem,theme(width.bounds))]',
    'max-w-bounds',
    'rounded-themeCornerSize',
    'leading-[1.2]',
    'justify-center'
  );

  useEffect(() => {
    if (!app.state.loaded) {
      return;
    }
    intervalRef.current = setInterval(() => {
      app.core
        .get('server')
        .get<{alive: boolean}>('/api/assetservice/token/status', {
          token: 'token',
          json: true,
        })
        .then(({data}) => {
          if (!data.alive) {
            setExpired(true);
            clearInterval(intervalRef.current);
          }
        });
    }, 5000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  if (!app.state.loaded) {
    return (
      <LoadingOverlay open={true} label={t('*:str_MessageStartingUploader')} usePortal={false} />
    );
  }

  return (
    <Theme
      name={'tpx-theme-main'}
      tipLabel={'Base'}
      allowBorder={false}
      className={windowClassNames}
    >
      <div>
        <Logo className="max-h-[calc(var(--tds-rem)*100)] mt-3" />
      </div>
      <Theme name={ThemeName.Section} className={contentPanelClasses}>
        <Heading size={2}>
          {t('str_TitleExternalUploadPictureUploader', {ns: 'uploadexternalassets'})}
        </Heading>
        <DeviceUploader expired={expired} />
      </Theme>

      <Dialog
        id={'tpx-upload-from-device-expiry'}
        open={expired}
        width={400}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        title={t('str_TitlePictureUploadClosed', {ns: 'uploadexternalassets'})}
      >
        <DialogContent id={'sessionExpiredDialogDescription'} className={'space-y-sm'}>
          <p>{t('str_MessagePictureUploadExpired', {ns: 'uploadexternalassets'})}</p>
        </DialogContent>
      </Dialog>
    </Theme>
  );
};

export default UploadFromDevice;
